import { Injectable, Signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as _ from 'underscore';

import { CmsService } from '../../../core/services/cms/cms.service';
import { ISystemConfig } from '../../../core/services/cms/models/system-config';
import { StorageService } from '../../../core/services/storage/storage.service';
import { IDateCoupon } from '../../../shared/components/marketSelector/market-selector.model';
import { ICouponCarousel } from '../couponsCarousel/coupons-carousel.model';
import { COUPON_NAMES_TYPE } from '../couponsDetails/coupons-details.constant';
import { ICoupon, ICouponCheck, ICouponsConfig } from '../couponsListSportTab/coupons.model';
import { COUPONS, DATED_COUPONS, DAYS, MATCHES, TODAY } from './coupons-list.constant';
import { ICouponData, ICouponSegment } from './coupons-list.model';

@Injectable({
    providedIn: 'root',
})
export class CouponsListService {
    private daysOfWeek: string[] = DAYS;
    couponData: ICouponData = {};
    exclusiveCoupon: ICoupon[] = [];
    sportName: string = 'football';
    private readonly defaultCouponsConfig: ICouponsConfig = {
        marketSpecificInclusion: '',
        marketSpecificExclusion: '',
        leagueCoupons: '',
        CorrectScore: '',
        BTTSInBothHalves: '',
        DoubleChance: '',
        DateCoupons: '',
        marketSpecificForUniqueIDs: '',
        marketSpecificDateExclusion: '',
    };
    private readonly couponsConfigSignal = toSignal(this.cmsService.getCouponsConfig(), { initialValue: this.defaultCouponsConfig });
    readonly cmConfigSignal = toSignal(this.cmsService.getMappingCMConfig(), { initialValue: [] });
    constructor(
        private cmsService: CmsService,
        public storage: StorageService,
    ) {}

    /**
     * Get Coupon Segment from CMS
     * @returns {Observable<ICouponSegment[]>}
     */
    getCouponSegment(): Observable<ICouponSegment[]> {
        return this.cmsService.getCouponSegment().pipe(
            map((data: ICouponSegment[]) => {
                return data;
            }),
        );
    }
    getCouponsConfigValue<K extends keyof ICouponsConfig>(key?: K): Signal<ICouponsConfig | ICouponsConfig[K] | ISystemConfig> {
        return computed(() => {
            const config = this.couponsConfigSignal();
            return key ? config[key] : config;
        });
    }
    containsValue<K extends keyof ICouponsConfig>(type: K, value: string | number): boolean {
        const config = this.couponsConfigSignal();
        const pattern = new RegExp(`(?:^|,)${value}(?:,|$)`);
        return pattern.test(config[type]);
    }
    isAdditionalMrkt(value: string): boolean {
        const datedList: string[] = [...COUPON_NAMES_TYPE.exclusive, ...COUPON_NAMES_TYPE.uniqids];
        return datedList.some((item) => item.toLowerCase() === value.toLowerCase());
    }
    isAdditionalcId(couponId: string, compare: string): boolean {
        const allUniqids =
            this.containsValue('CorrectScore', couponId) ||
            this.containsValue('DoubleChance', couponId) ||
            this.containsValue('BTTSInBothHalves', couponId);
        const allDateExclusive = this.containsValue('marketSpecificDateExclusion', couponId);
        const allleagues = this.containsValue(COUPONS.LEAGUE_COUPONS, couponId);
        switch (compare) {
            case 'legues':
                return allleagues;
            case 'uniqids':
                return allUniqids;
            case 'exclusive':
                return allDateExclusive;
            default:
                return allUniqids || allDateExclusive;
        }
    }
    /**
     * Get Coupon Segment from CMS
     * @returns {Observable<ICouponSegment[]>}
     */
    getCouponsConfig(): Observable<ICouponsConfig> {
        return this.cmsService.getCouponsConfig().pipe(
            map((data: ICouponsConfig) => {
                return data;
            }),
        );
    }

    /**
     * Get Coupon market mapping from CMS
     * @returns {Observable<string[]>}
     */
    getMappingCouponsMarketConfig(): Observable<string[]> {
        return this.cmsService.getMappingCMConfig().pipe(
            map((data: string[]) => {
                return data;
            }),
        );
    }

    /**
     * Get Coupon Carousel from CMS
     * @returns {Observable<ICouponSegment[]>}
     */
    getCouponCarousel(): Observable<ICouponCarousel[]> {
        return this.cmsService.getCouponCarousel().pipe(
            map((data: ICouponCarousel[]) => {
                return data;
            }),
        );
    }

    /**
     * Group Coupon By Segment
     * @param {ICoupon[]} coupons
     * @param {ICouponSegment[]} couponSegments
     * @returns {ICouponSegment[]}
     */
    groupCouponBySegment(coupons: ICoupon[], couponSegments: ICouponSegment[]): ICouponSegment[] {
        if (couponSegments && couponSegments.length) {
            const couponSegment = this.getCouponByDate(couponSegments) as ICouponSegment;
            if (!_.isEmpty(couponSegment)) {
                couponSegment.couponKeys = _.uniq((couponSegment.couponKeys as string).split(','));
                couponSegment.coupons = [];
                _.each(couponSegment.couponKeys, (id: string) => {
                    const coupon: ICoupon = _.findWhere(coupons, { id }) || ({} as ICoupon);
                    if (_.isObject(coupon) && Object.keys(coupon).length) {
                        couponSegment?.coupons?.push(coupon as ICoupon);
                    }
                });
                couponSegment.coupons = this.sortCoupons(couponSegment.coupons as ICoupon[]);
                return couponSegment.coupons.length > 0 ? [couponSegment] : [];
            }
            return [];
        } else {
            return [];
        }
    }
    sortDatedCoupons(dc: IDateCoupon[]): IDateCoupon[] {
        const desiredOrder = ['today', 'tomorrow', 'midweek', 'weekend'];
        return dc.sort((a, b) => desiredOrder.indexOf(a.templateMarketName) - desiredOrder.indexOf(b.templateMarketName));
    }
    /**
     * Group Carousels
     * @param carousels - carousel list
     * @returns - Grouped Carousel list
     */
    groupCarousel(carousels: ICouponCarousel[]): ICouponCarousel[] {
        return carousels
            .map((carousel: ICouponCarousel) => {
                const coupon = this.getCouponByDate([carousel]);
                return Object.keys(coupon).length ? coupon : undefined;
            })
            .filter((coupon): coupon is ICouponCarousel => coupon !== undefined);
    }

    /**
     * Sort coupons data
     * @param {ICoupon[]} coupons
     * @returns {ICoupon[]}
     */
    private sortCoupons(coupons: ICoupon[]): ICoupon[] {
        return coupons.length
            ? _.chain(coupons)
                  .sortBy((data) => data?.name?.toLowerCase())
                  .sortBy('displayOrder')
                  .value()
            : [];
    }

    /**
     * Get Coupon By Date
     * @param {ICouponSegment[]} couponSegment
     * @returns {ICouponSegment}
     */
    private getCouponByDate(couponSegment: ICouponSegment[] | ICouponCarousel[]): ICouponSegment | ICouponCarousel {
        const couponByDateRange = this.getCouponByDateRange(couponSegment);
        const getCouponByDay = this.getCouponByDay(couponSegment);
        return (!_.isEmpty(couponByDateRange) && couponByDateRange) || (!_.isEmpty(getCouponByDay) && getCouponByDay) || ({} as ICouponSegment);
    }

    /**
     * Get Coupon By Date Range (from,to)
     * @param {ICouponSegment[]} couponSegment
     * @returns {ICouponSegment}
     */
    private getCouponByDateRange(couponSegment: ICouponSegment[]): ICouponSegment {
        const couponsByRange = couponSegment.filter((segment: ICouponSegment) => {
            if (segment.from && segment.to) {
                const nowDate = new Date(new Date()).getTime();
                const fromDate = this.getCorrectTime(segment.from);
                const toDate = this.getCorrectTime(segment.to);
                return fromDate <= nowDate && nowDate <= toDate;
            }
            return false;
        });
        return couponsByRange && couponsByRange.length ? couponsByRange[0] : ({} as ICouponSegment);
    }

    /**
     * Get Correct time
     * @param {string} date
     * @returns {number}
     */
    private getCorrectTime(date: string): number {
        const a = date.split(/[^0-9]/).map(Number);
        return new Date(a[0], a[1] - 1, a[2], a[3], a[4], a[5]).getTime();
    }

    /**
     * Get Coupon By Day ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
     * @param {ICouponSegment[]} couponSegment
     * @returns {ICouponSegment}
     */
    private getCouponByDay(couponSegment: ICouponSegment[]): ICouponSegment {
        const dayIndex = new Date().getDay();
        const dayName = this.daysOfWeek[dayIndex];
        const couponsByDay = _.filter(couponSegment, (segment: ICouponSegment) => {
            if (segment.dayOfWeek) {
                return segment.dayOfWeek.indexOf(dayName) >= 0;
            }
            return false;
        });
        return couponsByDay && couponsByDay.length ? couponsByDay[0] : ({} as ICouponSegment);
    }

    /**
     * Method to check if current coupon is in the list
     * @param couponId - coupon id
     * @param selectedCoupon - selected coupon
     * @returns ICouponCheck
     */
    public checkCurrentCoupon(couponId: string, selectedCoupon: string, previousCoupon?: string): ICouponCheck {
        const couponList = this.getConfigList(couponId);
        const labelMap: Record<string, { index: number; name: string }> = {
            Today: { index: 0, name: MATCHES.TODAY },
            Tomorrow: { index: 1, name: MATCHES.TOMORROW },
            Midweek: { index: 2, name: MATCHES.MIDWEEK },
            Weekend: { index: 3, name: MATCHES.WEEKEND },
        };
        let { index, name } = labelMap[previousCoupon as string] || labelMap[selectedCoupon] || labelMap[TODAY];
        name = selectedCoupon;
        return {
            id: couponList[index] || null,
            name,
        };
    }
    getConfigList(couponId: string): string[] {
        const couponConfig = this.getCouponsConfigValue()();
        const configTypes: Array<keyof ICouponsConfig> = [
            COUPONS.CORRECT_SCORE as keyof ICouponsConfig,
            COUPONS.BBTS as keyof ICouponsConfig,
            COUPONS.DOUBLE_CHANCE as keyof ICouponsConfig,
        ];
        const selectedType = configTypes.find((type) => this.containsValue(type, couponId)) as keyof ICouponsConfig;
        const couponList =
            selectedType && typeof couponConfig === 'object' && 'marketSpecificInclusion' in couponConfig
                ? couponConfig[selectedType]?.split(',') || []
                : [];
        return couponList;
    }

    /**
     * To set store market by default
     * @param coupon ICoupon | Partial<ICoupon>
     * @param selected - option
     */
    public setStoreDefault(coupon: ICoupon | Partial<ICoupon>, selected: string): void {
        this.couponData[this.sportName] = {
            selectedOption: selected,
            storedCoupon: coupon.id || '',
        };
        this.storage.set('storeCouponMarket', this.couponData);
    }

    /**
     * To get Coupon config from CMS
     * @returns - Observable<ISystemConfig>
     */
    getCouponCmsConfig(): Observable<ISystemConfig> {
        return this.cmsService.getFeatureConfig('CouponConfig');
    }
    getCouponFilterName(couponFilter: string): string {
        const finalCD = couponFilter
            .replace(/(Tomorrow)(\'s)? Matches/i, DATED_COUPONS.TOMORROW)
            .replace(/(Today)(\'s)? Matches/i, DATED_COUPONS.TODAY)
            .replace(/Midweek Matches/i, DATED_COUPONS.MIDWEEK)
            .replace(/Weekend Matches/i, DATED_COUPONS.WEEKEND);
        const cdheck = finalCD.split(' ');

        return cdheck.length > 1 ? cdheck[0] : finalCD;
    }
}
